import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTheme, alpha } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { getAllPresupuestoA3Pedidos } from "../../api/Produccion/a3ArticulosApi";
import { getLogisticaEnviosResumen } from "../../api/Logistica/logisticaEnviosApi";
import { enviosAjenosDePedido } from "./logisticaUtils";

function isA3Served(estado, detalle) {
  const s = String(estado || "")
    .trim()
    .toUpperCase();
  const d = String(detalle || "")
    .trim()
    .toUpperCase();
  return s === "S" || d === "S" || s.includes("SERV") || d.includes("SERV");
}

const listPaperSx = (theme) => ({
  maxHeight: 420,
  overflow: "auto",
  borderRadius: 2,
  border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
  bgcolor: alpha(theme.palette.background.paper, 0.95),
  "& .MuiListItemButton-root": {
    borderRadius: 1,
    mx: 0.5,
    my: 0.25,
  },
  "& .MuiListItemButton-root.Mui-selected": {
    bgcolor: alpha(theme.palette.primary.main, 0.12),
    borderLeft: `3px solid ${theme.palette.primary.main}`,
  },
});

/**
 * Elige un pedido CRM con venta A3 (IdPedv) para abrir el mismo
 * PedidoEnviosDialog que en Pedidos.
 */
const SeleccionarPedidoNuevoEnvioDialog = ({ open, onClose, onSelect }) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [allRows, setAllRows] = useState([]);
  const [enviosRows, setEnviosRows] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [hideServed, setHideServed] = useState(false);

  const eligible = useMemo(() => {
    const list = Array.isArray(allRows) ? allRows : [];
    return list.filter((r) => {
      if (!r?.IdPresupuestoA3Pedido) return false;
      if (!(r?.IdPedv != null && Number(r.IdPedv) > 0)) return false;
      if (hideServed && isA3Served(r.EstadoVenta, r.EstadoDetalleVenta)) {
        return false;
      }
      return true;
    });
  }, [allRows, hideServed]);

  const filtered = useMemo(() => {
    const q = String(search || "")
      .trim()
      .toLowerCase();
    if (!q) return eligible;
    return eligible.filter((r) => {
      const blob = [
        r.Referencia,
        r.ClienteNombre,
        r.CodCli,
        r.CodArt,
        r.CodPro,
        r.NombrePresupuesto,
        r.ProyectoSubject,
        r.IdPresupuestoA3Pedido,
        r.IdPresupuesto,
        r.IdProducto,
        r.NumDocVenta,
        r.SerieVenta,
      ]
        .map((x) => String(x ?? "").toLowerCase())
        .join(" ");
      return blob.includes(q);
    });
  }, [eligible, search]);

  const selected = useMemo(() => {
    const id = Number(selectedId);
    const list = Array.isArray(allRows) ? allRows : [];
    return list.find((x) => Number(x.IdPresupuestoA3Pedido) === id) || null;
  }, [allRows, selectedId]);

  const reset = useCallback(() => {
    setSearch("");
    setSelectedId(null);
    setError("");
    setHideServed(false);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [data, envios] = await Promise.all([
        getAllPresupuestoA3Pedidos(2000),
        getLogisticaEnviosResumen({ limit: 800, includeClosed: true }).catch(
          () => [],
        ),
      ]);
      setAllRows(Array.isArray(data?.rows) ? data.rows : []);
      setEnviosRows(Array.isArray(envios) ? envios : envios?.rows || []);
    } catch (e) {
      setError(e?.response?.data?.error || e.message);
      setAllRows([]);
      setEnviosRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) {
      reset();
      setAllRows([]);
      setEnviosRows([]);
      return;
    }
    reset();
    load();
  }, [open, load, reset]);

  const handleContinue = () => {
    if (!selected) return;
    onSelect(selected);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      scroll="paper"
    >
      <DialogTitle>{t("logisticaPedidos.nuevoEnvioPickTitle")}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={1.5}>
          <Alert severity="info">
            {t("logisticaPedidos.nuevoEnvioPickHint")}
          </Alert>
          {error ? (
            <Alert severity="error" onClose={() => setError("")}>
              {error}
            </Alert>
          ) : null}
          <FormControlLabel
            control={
              <Checkbox
                checked={hideServed}
                onChange={(e) => {
                  setHideServed(e.target.checked);
                  setSelectedId(null);
                }}
                color="primary"
              />
            }
            label={t("logisticaPedidos.vincularFilterServed")}
          />
          <TextField
            size="small"
            fullWidth
            placeholder={t("logisticaPedidos.vincularSearchPh")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" color="action" />
                </InputAdornment>
              ),
            }}
          />
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={2}
            alignItems="stretch"
          >
            <Box sx={{ flex: "1 1 55%", minWidth: 0 }}>
              <Paper variant="outlined" sx={listPaperSx(theme)}>
                {loading ? (
                  <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                    <CircularProgress size={32} />
                  </Box>
                ) : filtered.length === 0 ? (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ p: 2 }}
                  >
                    {t("logisticaPedidos.nuevoEnvioPickEmpty")}
                  </Typography>
                ) : (
                  <List dense disablePadding sx={{ py: 0.5 }}>
                    {filtered.map((r) => {
                      const id = Number(r.IdPresupuestoA3Pedido);
                      const isSel = Number(selectedId) === id;
                      const served = isA3Served(
                        r.EstadoVenta,
                        r.EstadoDetalleVenta,
                      );
                      const enviosPed = enviosAjenosDePedido(enviosRows, id, 0);
                      return (
                        <ListItemButton
                          key={id}
                          selected={isSel}
                          onClick={() => setSelectedId(id)}
                          onDoubleClick={() => {
                            setSelectedId(id);
                            onSelect(r);
                            onClose();
                          }}
                        >
                          <ListItemText
                            primary={
                              <Stack
                                direction="row"
                                alignItems="center"
                                gap={0.75}
                                flexWrap="wrap"
                              >
                                <Typography component="span" fontWeight={700}>
                                  {r.Referencia ||
                                    t("logisticaPedidos.vincularSinRef")}
                                </Typography>
                                <Chip
                                  size="small"
                                  label={`CRM #${id}`}
                                  variant="outlined"
                                />
                                {enviosPed.length ? (
                                  <Chip
                                    size="small"
                                    color="info"
                                    variant="outlined"
                                    label={t(
                                      "logisticaPedidos.vincularHasEnvioChip",
                                      {
                                        envios: enviosPed
                                          .map((x) => `#${x.IdLogisticaEnvio}`)
                                          .join(", "),
                                      },
                                    )}
                                  />
                                ) : null}
                                {served ? (
                                  <Chip
                                    size="small"
                                    color="warning"
                                    variant="outlined"
                                    label={t(
                                      "logisticaPedidos.vincularServedChip",
                                    )}
                                  />
                                ) : null}
                              </Stack>
                            }
                            secondary={
                              <>
                                <Typography
                                  component="span"
                                  variant="caption"
                                  display="block"
                                >
                                  {r.ClienteNombre || r.CodCli || "—"} ·{" "}
                                  {r.CodArt || "—"} · Prov. {r.CodPro || "—"}
                                </Typography>
                                <Typography
                                  component="span"
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  {r.NombrePresupuesto || "—"}
                                  {r.ProyectoSubject
                                    ? ` · ${r.ProyectoSubject}`
                                    : ""}{" "}
                                  · ProductId {r.IdProducto ?? "—"}
                                </Typography>
                              </>
                            }
                            secondaryTypographyProps={{ component: "div" }}
                          />
                        </ListItemButton>
                      );
                    })}
                  </List>
                )}
              </Paper>
            </Box>
            <Box sx={{ flex: "1 1 45%", minWidth: 0 }}>
              <Paper
                variant="outlined"
                sx={{ p: 2, borderRadius: 2, minHeight: 180 }}
              >
                {!selected ? (
                  <Typography variant="body2" color="text.secondary">
                    {t("logisticaPedidos.vincularPickHint")}
                  </Typography>
                ) : (
                  <Stack spacing={1}>
                    <Typography variant="body1" fontWeight={700}>
                      {selected.Referencia || "—"}
                    </Typography>
                    <Typography variant="body2">
                      {t("logisticaPedidos.vincularCliente")}:{" "}
                      {selected.ClienteNombre || selected.CodCli || "—"}
                    </Typography>
                    <Typography variant="body2">
                      {t("logisticaPedidos.vincularArticulo")}:{" "}
                      {selected.CodArt || "—"}
                    </Typography>
                    <Typography variant="body2">
                      {t("logisticaPedidos.vincularProv")}:{" "}
                      {selected.CodPro || "—"}
                    </Typography>
                  </Stack>
                )}
              </Paper>
            </Box>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t("logisticaPedidos.cancel")}</Button>
        <Button
          variant="contained"
          onClick={handleContinue}
          disabled={!selected}
        >
          {t("logisticaPedidos.nuevoEnvioPickContinue")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SeleccionarPedidoNuevoEnvioDialog;
